import React, { useState, useEffect } from "react";
import "./CashBank.css"; // Import styling for the table
import axios from "axios";
import { faAdd, faChevronDown, faChevronUp, faPencil, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { confirmDelete } from "../../../../helpers/PromptUtils";
import { getToken } from "../../../../helpers/Jwt";
import ButtonOptionGroup from "../../component/ButtonOptions/ButtonOption";
import { formatCurrency } from "../../../../helpers/CurrencyInput";
import { faInfo } from "@fortawesome/free-solid-svg-icons";

const CashBank = () => {
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [amount, setAmount] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const PATH_ACCOUNT = "api/erpppsu/account/cashbank";
    const PATH_AMOUNT = "api/erpppsu/account/amount";

    const [expandedAccounts, setExpandedAccounts] = useState({});

    const fetchData = async (URL) => {
        try {
            const response = await axios.get(URL);
            return response.data;
        } catch (err) {
            setError("Failed to fetch data. Please try again later.");
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const groupAccountsByParent = (accounts) => {
        const grouped = {};

        accounts.forEach((account) => {
            if (account.parent_account === null) {
                grouped[account.code] = {
                    ...account,
                    subAccounts: [],
                };
            } else {
                // If the account is a sub-account
                if (!grouped[account.parent_account]) {
                    grouped[account.parent_account] = {
                        subAccounts: [],
                    };
                }
                grouped[account.parent_account].subAccounts.push(account);
            }
        });

        // Return grouped accounts, including parent accounts and their sub-accounts
        return Object.values(grouped);
    };

    // Handle expansion toggle
    const toggleExpand = (parentCode) => {
        setExpandedAccounts((prevState) => ({
            ...prevState,
            [parentCode]: !prevState[parentCode],
        }));
    };

    const renderAccountRow = (account) => {
        const handleAction = (rowData, callback) => {
            callback(rowData);
        };

        const CurrencyDisplay = ({ amount, currency }) => {
            const { text, isNegative } = formatCurrency(amount, currency, true);

            return (
                <span
                    className={`font-bold ${isNegative ? 'text-red-600' : 'text-emerald-600'}`}>
                    {amount.toLocaleString("id-ID", { minimumFractionDigits: 2 })}
                </span>
            );
        };

        return (
            <React.Fragment key={account.id}>
                <tr className="parent-account-row">
                    <td>{account.code}</td>
                    <td>{account.name}</td>
                    <td>{account.category}</td>
                    <td><CurrencyDisplay amount={account.current_balance} currency={'IDR'} /></td>
                    
                    <td>
                        <div className="action-buttons">
                            {actions?.map((action, index) => {
                                return (
                                    <button
                                        key={index}
                                        onClick={() => handleAction(account, action.callback)}
                                        className={`m-1 action-btn ${action.className}`}
                                    >
                                        {action.icon}
                                        {action.text}
                                    </button>
                                );
                            })}
                        </div>
                    </td>
                </tr>

                {/* Toggle button for parent account */}
                {account.subAccounts && account.subAccounts.length > 0 && (
                    <tr className="expand-row">
                        <td colSpan="5">
                            <button
                                onClick={() => toggleExpand(account.code)}
                                className="expand-btn"
                            >
                                <FontAwesomeIcon icon={expandedAccounts[account.code] ? faChevronUp : faChevronDown} />
                                {expandedAccounts[account.code] ? " Sembunyikan" : " Tampilkan"} Sub-Akun
                            </button>
                        </td>
                    </tr>
                )}

                {/* Render sub-accounts if expanded */}
                {expandedAccounts[account.code] && account.subAccounts && account.subAccounts.map((subAccount) => (
                    <tr key={subAccount.id} className="sub-account-row">
                        <td><p className="text-center">{subAccount.code}</p></td>
                        <td>{subAccount.name}</td>
                        <td>{subAccount.category}</td>
                        <td>
                            <CurrencyDisplay amount={subAccount.current_balance} currency={'IDR'} /></td>
                        <td>
                            <div className="action-buttons">
                                {actions?.map((action, index) => {
                                    return (
                                        <button
                                            key={index}
                                            onClick={() => handleAction(subAccount, action.callback)}
                                            className={`m-1 action-btn ${action.className}`}
                                        >
                                            {action.icon}
                                            {action.text}
                                        </button>
                                    );
                                })}
                            </div>
                        </td>
                    </tr>
                ))}
            </React.Fragment>
        );
    };

    useEffect(() => {
        fetchData(`${BASE_URL}${PATH_ACCOUNT}`).then((accounts) => {
            if (accounts) {
                const groupedAccounts = groupAccountsByParent(accounts);
                setData(groupedAccounts);
            }
        });

        fetchData(`${BASE_URL}${PATH_AMOUNT}`).then((data) => {
            setAmount(data);
        });
    }, []);
    
    const handleDetail = (rowData) => {
        navigate(`/activity-erpppsu/cash-bank/detail/${rowData.code}`);
    };

    const actions = [
        {
            className: 'info-btn',
            icon: <FontAwesomeIcon icon={faInfo} />,
            text: ' Info',
            callback: handleDetail,
        },
    ];

    const options = [
        { value: 'add-account', label: 'Akun Baru' },
        { value: 'transfer', label: 'Transfer Uang' },
        { value: 'bank-deposit', label: 'Terima Uang' },
        { value: 'withdrawal', label: 'Kirim Uang' }
    ];

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <div>
            <div className='flex items-center justify-between'>
                <h1>Kas & Bank</h1>

                <div className='flex items-center'>

                    <ButtonOptionGroup
                        label={"Buat Akun/Transaksi"}
                        options={options}
                        onChange={(value) => {
                            if (value == 'add-account') {
                                navigate('/activity-erpppsu/add-account');
                            } else if (value == 'transfer') {
                                navigate(`/activity-erpppsu/cash-bank/transaction/transfer`);
                            } else if (value == 'bank-deposit' || value == 'withdrawal') {
                                navigate(`/activity-erpppsu/cash-bank/transaction/${value}`);
                            }
                        }}
                    />
                </div>
            </div>

            <div className="flex flex-wrap gap-4">
                {amount?.map((card, index) => (
                    <div
                        key={index}
                        className={`w-96 p-4 rounded-lg shadow-md ${card.bgColor}`}
                    >
                        <div className="flex justify-between items-center">
                            <h3 className="text-sm font-medium text-gray-800">{card.title}</h3>
                            <span
                                className={`text-xs font-bold px-2 py-1 rounded-full ${card.textColor} bg-white`}
                            >
                                {card.badge}
                            </span>
                        </div>
                        <p className="text-lg font-semibold text-gray-900 mt-4">
                            Total
                        </p>
                        <p className="text-2xl font-bold mt-2">Rp{card.total.toLocaleString("id-ID", { minimumFractionDigits: 2 })}</p>
                    </div>
                ))}
            </div>
            <div className="relative overflow-x-auto">
                <table className="account-table min-w-full bg-white border border-gray-200 p-2">
                    <thead>
                        <tr>
                            <th className="text-gray-500 font-medium text-xs uppercase">Kode Akun</th>
                            <th className="text-gray-500 font-medium text-xs uppercase">Nama Akun</th>
                            <th className="text-gray-500 font-medium text-xs uppercase">Kategori Akun</th>
                            <th className="text-gray-500 font-medium text-xs uppercase">Saldo</th>
                            <th className="text-gray-500 font-medium text-xs uppercase">Aksi</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                        {data.map((account) => renderAccountRow(account))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CashBank;
